import GenericForm from "@/components/cms/GenericForm";
import StyledDrawer from "@/components/StyledDrawer";
import { Button } from "@/components/ui/button";
import { CustomDialogFooter } from "@/components/ui/custom-dialog";
import { SheetClose } from "@/components/ui/sheet";
import {
  Menubar,
  MenubarContent,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { FormFieldVisibilityProvider } from "@/context/FormFieldVisibilityContext";
import { FormVisibilityMenuItems } from "@/components/form-items/FormVisibilityMenuItems";
import { FormLayoutElement } from "@/config/forms/general";
import { getAttachmentsByDocId } from "@/api/attachment";
import { getDefaultImage, TypeDefaultImage } from "@/lib/image";
import { Attachment } from "@/lib/types";
import { useQuery } from "@tanstack/react-query";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { Edit2, PlusCircle } from "lucide-react";
import { ReactNode, useMemo, useState } from "react";
import { FieldValues, FormProvider, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

type LinksDrawerLayoutProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  fields: FormLayoutElement[] | undefined;
  recordId: string | undefined | null;
  onSubmit: (data: T) => void;
  previewImage?: {
    attachments?: Attachment[];
    type: TypeDefaultImage;
    docId?: string;
    alt?: string;
  };
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: ReactNode;
  /** Optional left sidebar (e.g. itinerant exhibition management) */
  sidebar?: ReactNode;
  /** Rendered under the form (e.g. itinerant CF preview replacing classic Notes) */
  formFooter?: ReactNode;
  /** Wider drawer when sidebar is present; override with maxWidth */
  maxWidth?: string;
  /** Disable submit (e.g. while syncing itinerant group) */
  submitDisabled?: boolean;
};

const LinksDrawerLayout = <T extends FieldValues>({
  form,
  fields,
  recordId,
  onSubmit,
  previewImage,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  trigger,
  sidebar,
  formFooter,
  maxWidth,
  submitDisabled,
}: LinksDrawerLayoutProps<T>) => {
  const { t } = useTranslation();
  const { getValues, control, handleSubmit } = form;

  const [open, setOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const sheetOpen = isControlled ? controlledOpen : open;
  const setSheetOpen = controlledOnOpenChange ?? setOpen;
  const resolvedTrigger =
    trigger ??
    (isControlled ? undefined : (
      <Button className="font-semibold flex gap-2 p-1" variant="ghost">
        {recordId ? (
          <Edit2 className="w-5 h-5 cursor-pointer text-gray-500" />
        ) : (
          <PlusCircle className="w-5 h-5 cursor-pointer text-[#005D8D]" />
        )}
      </Button>
    ));
  const { data: fetchedAttachments } = useQuery({
    queryKey: ["drawer-attachments", previewImage?.docId],
    queryFn: () => getAttachmentsByDocId(previewImage?.docId ?? ""),
    enabled:
      !!previewImage?.docId &&
      (!previewImage.attachments || previewImage.attachments.length === 0),
  });

  const previewSrc = useMemo(() => {
    if (!previewImage?.type) return null;
    const sourceAttachments =
      previewImage.attachments && previewImage.attachments.length > 0
        ? previewImage.attachments
        : (fetchedAttachments ?? []);
    if (sourceAttachments.length === 0) return null;
    return getDefaultImage(sourceAttachments, previewImage.type, "mediumPublicUrl");
  }, [fetchedAttachments, previewImage]);

  const localHandleSubmit = (data: T) => {
    try {
      onSubmit(data);
      setSheetOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const resolvedMaxWidth =
    maxWidth ?? (sidebar ? "sm:max-w-6xl" : undefined);

  return (
    <FormFieldVisibilityProvider formKey="links-drawer">
      <StyledDrawer
        sheetProps={{
          open: sheetOpen,
          onOpenChange: setSheetOpen,
        }}
        title={recordId ? t("editLinkTitle") : t("createLinkTitle")}
        className="flex flex-col justify-between overflow-y-auto"
        maxWidth={resolvedMaxWidth}
        trigger={resolvedTrigger}
        headerActions={
          <Menubar className="border-none p-0">
            <MenubarMenu>
              <MenubarTrigger className="px-3 py-2 rounded-lg justify-center items-center gap-2 flex border border-input bg-white hover:bg-slate-100 whitespace-nowrap min-w-fit cursor-pointer">
                <p className="text-sm font-semibold font-['Raleway']">
                  {t("actions")}
                </p>
                <ChevronDownIcon className="w-5 h-5" />
              </MenubarTrigger>
              <MenubarContent>
                <FormVisibilityMenuItems tabs={[]} />
              </MenubarContent>
            </MenubarMenu>
          </Menubar>
        }
        footer={
          <CustomDialogFooter>
            <SheetClose asChild>
              <Button className="font-semibold w-full border" variant="ghost">
                {t("cancel")}
              </Button>
            </SheetClose>
            <Button
              className="font-semibold w-full"
              variant="default"
              disabled={submitDisabled}
              onClick={handleSubmit(localHandleSubmit)}
            >
              {recordId ? t("editLink") : t("createLink")}
            </Button>
          </CustomDialogFooter>
        }
      >
        <div
          className={cn(
            sidebar && "grid grid-cols-1 md:grid-cols-2 gap-4 min-h-0",
          )}
        >
          {sidebar}
          <div className="min-w-0 flex flex-col">
            {previewSrc && (
              <img
                src={previewSrc}
                alt={previewImage?.alt ?? "preview"}
                className="w-full h-fit object-contain mb-2"
              />
            )}
            <FormProvider {...form}>
              <GenericForm
                getValues={getValues}
                control={control}
                configuration={{ fields: fields ?? [], onSubmit: () => {} }}
              />
            </FormProvider>
            {formFooter ? (
              <div className="mt-4 w-full">{formFooter}</div>
            ) : null}
          </div>
        </div>
      </StyledDrawer>
    </FormFieldVisibilityProvider>
  );
};

export default LinksDrawerLayout;
